/**
 * There is a smattering of force casting in this file. All of it is done to
 * circumvent the handling of generics with both React.memo and
 * styled-components.
 *
 * Being that we'd like to allow flexibility with the items leveraged in our
 * Omnibar, but have existing functionality built on the assumption it's always
 * set to ICommand, we set that to the default and otherwise expose a type
 * parameter for new consumers.
 */
import { Classes, MaybeElement } from "@blueprintjs/core";
import { Omnibar, OmnibarProps } from "@blueprintjs/select";
import React from "react";
import styled from "styled-components";

export interface ICommand {
  title: string;
  icon: MaybeElement;
  category: string;
  combo: string;
  mode: "all" | "command" | "edit";
  disabled: boolean;
  visible?: boolean;
  onAction: () => void;
}

interface HexPortalOmnibarProps<T> extends OmnibarProps<T> {
  realClassName?: string;
}

const HexPortalOmnibar_ = <T = ICommand,>({
  className,
  realClassName,
  ...props
}: HexPortalOmnibarProps<T>): JSX.Element => {
  return (
    <Omnibar<T>
      {...props}
      className={realClassName}
      overlayProps={{
        portalClassName: className,
        transitionDuration: 50,
        // Monaco has a bug where it doesn't properly return focus after the omnibar
        //overlay is closed. So we just disable this behavior and manage things ourselves.
        // See https://github.com/palantir/blueprint/issues/5857.
        shouldReturnFocusOnClose: false,
      }}
    />
  );
};

/**
 * This component is an intermediate component that allows us to use
 * styled components with a class prop that isn't `className`.
 *
 * We bascially just save off the consumer `className` to `realClassName`, applying the
 * styles added by styled components to `portalClassName`.
 */
export const HexPortalOmnibar = React.memo(
  HexPortalOmnibar_,
) as typeof HexPortalOmnibar_;

const OMNIBAR_WIDTH = 360;

const StyledHexPortalOmnibar = styled(HexPortalOmnibar)`
  && {
    > .${Classes.OVERLAY} {
      > .${Classes.OVERLAY_CONTENT} {
        left: calc(50% - ${OMNIBAR_WIDTH / 2}px);

        width: ${OMNIBAR_WIDTH}px;

        background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
        box-shadow: ${({ theme }) => theme.boxShadow.DIALOG};

        filter: blur(0) !important;
      }

      .${Classes.INPUT} {
        color: ${({ theme }) => theme.fontColor.DEFAULT};
      }

      .${Classes.MENU} {
        color: ${({ theme }) => theme.fontColor.DEFAULT};

        box-shadow: inset 0 1px 0 ${({ theme }) => theme.borderColor.MUTED};
      }

      > .${Classes.OVERLAY_BACKDROP} {
        background: none;
      }
    }
  }
` as typeof HexPortalOmnibar;

const HexOmnibar_ = <T = ICommand,>({
  className,
  ...props
}: OmnibarProps<T>): JSX.Element => (
  <StyledHexPortalOmnibar<T> {...props} realClassName={className} />
);

export const HexOmnibar = React.memo(HexOmnibar_) as typeof HexOmnibar_;
